import React from "react";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";

import { Text } from "src/library/components/Text";
import { Datetime } from "src/library/components/Datetime";
import { formatPrice } from "src/core/prices/utils";
import { Button } from "src/library/components/Button";
import {
  usePaymentApi,
  IPaymentCard
} from "src/modules/payment/api/payment.api";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { PaymentTransaction } from "src/modules/payment/api/transactions.api";
import { ThreeDSecureModal } from "src/modules/payment/components/ThreeDSecureModal";
import { InfoBar } from "src/library/components/InfoBar";

import PayInvoiceLaterWarningModal from "./PayInvoiceLaterWarningModal";
import { Invoice } from "../../api/accounting.api";

export interface PayInvoiceSummaryProps {
  invoice: Invoice;
  selectedPaymentCard: IPaymentCard | null;
  onPaymentSuccess?: () => void;
}

const PayInvoiceSummary: React.FunctionComponent<PayInvoiceSummaryProps> = ({
  invoice,
  selectedPaymentCard,
  onPaymentSuccess
}) => {
  const history = useHistory();
  const [ showPayLaterWarningModal, setShowPayLaterWarningModal ] = React.useState(false);
  const [ currentTransaction, setCurrentTransaction ] = React.useState<PaymentTransaction | null>(null);
  const [ payForInvoiceRes, payForInvoiceReq ] = usePaymentApi("CREATE_INVOICE_TRANSACTION");
  const subtotal = invoice.totalAmount - invoice.vatAmount;
  const orderCount = invoice.orderCount || 0;

  const summaryInfobarItems = React.useMemo(() => {
    return [
      {
        label: "Invoice date",
        value: invoice.invoiceDate ? (
          <Datetime
            ignoreLocal
            template="long_date"
            date={invoice.invoiceDate}
          />
        ) : "-"
      },
      {
        label: "Invoice number",
        value: invoice.invoiceNumber || "-"
      },
      {
        label: `Subtotal ${orderCount} order${orderCount !== 1 ? "s" : ""}`,
        value: (
          <Text weight="bold">
            {formatPrice(subtotal, 2)}
          </Text>
        )
      },
      {
        label: "Vat",
        value: (
          <Text>
            {formatPrice(invoice.vatAmount, 2)}
          </Text>
        )
      }
    ];
  }, [
    invoice,
    orderCount,
    subtotal
  ]);

  const canPayForInvoiceMessage = React.useMemo(() => {
    if (invoice.status === "paid") {
      return "This invoice has already been paid";
    }

    if (invoice.status === "cancelled") {
      return "This invoice has been cancelled";
    }

    return null;
  }, [ invoice.status ]);

  const payForInvoice = React.useCallback(() => {
    if (invoice.id && selectedPaymentCard?.id) {
      payForInvoiceReq({
        invoiceId: invoice.id,
        paymentCardId: selectedPaymentCard.id
      });
    }
  }, [
    invoice.id,
    payForInvoiceReq,
    selectedPaymentCard
  ]);

  React.useEffect(() => {
    if (payForInvoiceRes.data) {
      setCurrentTransaction(payForInvoiceRes.data);

      if (!payForInvoiceRes.data.nextAction) {
        if (onPaymentSuccess) {
          onPaymentSuccess();
        } else {
          history.push(`/admin/accounting/invoices/${invoice.id}`);
        }
      }
    }
  }, [
    history,
    invoice.id,
    onPaymentSuccess,
    payForInvoiceRes.data
  ]);

  const currentErrorMessage = React.useMemo(() => {
    if (payForInvoiceRes.error) {
      return payForInvoiceRes.error.message;
    }

    if (currentTransaction?.errorReason?.message) {
      return currentTransaction.errorReason.message;
    }

    return null;
  }, [ currentTransaction?.errorReason?.message, payForInvoiceRes.error ]);

  return (
    <StyledWrapper>
      <InfoBar
        title="Summary"
        items={summaryInfobarItems}
        listBorderBottom
        disableExpand
      >
        <div className="order-total">
          <div className="item-text">
            <Text as="h3">
              Order Total
            </Text>
          </div>

          <div className="item-text">
            <Text as="h3">
              {formatPrice(invoice.totalAmount, 2)}
            </Text>
          </div>
        </div>

        <div className="action-buttons">
          <Button
            onClick={() => payForInvoice()}
            $buttonType="primary"
            loading={payForInvoiceRes.pending}
            disabled={!selectedPaymentCard?.id || !!canPayForInvoiceMessage}
          >
            Confirm and Pay
          </Button>

          {canPayForInvoiceMessage && (
            <ErrorNotice
              type="warn"
              message={canPayForInvoiceMessage}
            />
          )}

          <Button
            loading={payForInvoiceRes.pending}
            disabled={!!canPayForInvoiceMessage}
            onClick={() => setShowPayLaterWarningModal(true)}
            $buttonType="secondary"
          >
            Pay Later
          </Button>

          {currentErrorMessage && (
            <ErrorNotice
              type="error"
              message={currentErrorMessage}
            />
          )}

          <Button
            onClick={() => history.push(`/admin/accounting/invoices/${invoice.id}`)}
            $buttonType="plain"
          >
            Back to Edit order
          </Button>
        </div>
      </InfoBar>

      <PayInvoiceLaterWarningModal
        open={showPayLaterWarningModal}
        onDismiss={() => setShowPayLaterWarningModal(false)}
        invoice={invoice}
      />

      <ThreeDSecureModal
        handleError={transaction => {
          if (transaction) {
            setCurrentTransaction(transaction);
          }
        }}
        onSuccess={() => {
          if (onPaymentSuccess) {
            onPaymentSuccess();
          } else {
            history.push(`/admin/accounting/invoices/${invoice.id}`);
          }
        }}
        secureUrl={currentTransaction?.nextAction?.action.url || null}
      />
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    .info-bar-content-wrapper {
      min-width: 380px;
    }

    .order-total {
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.lg};
      display: flex;
      align-content: center;
      
      .item-text {
        display: flex;
        justify-content: flex-end;
        flex: 1;
      }

      h3 {
        margin: 0;
      }
    }

    .action-buttons {
      padding: ${theme.spacing.padding.md};

      button {
        margin: ${theme.spacing.margins.sm} 0;
        width: 100%;
      }
    }

    @media (min-width: 1000px) {
      .info-wrapper {
        min-height: inherit;
        height: inherit;
        padding-bottom: 0;
      }
    }
  `}
`;

export default PayInvoiceSummary;
